import { useState } from "react";
import styled from "styled-components";
import { ContactDetails, State } from "../../types";
import { DetailsForm } from "./DetailsForm/DetailsForm";
import { IconButton, Logo, Title } from "../../components";
import { defaultThemeStyles } from "../../mocks";
import { ArrowLeftSolid } from "../../assets/icons";
import { useTranslation } from "react-i18next";

const StyledPageContainer = styled.div`
  display: grid;
  row-gap: 16px;
`;

const StyledHeader = styled.div`
  display: grid;
  grid-template-columns: 30px 1fr 30px;
  justify-content: center;
  align-items: center;
`;

const StyledTitle = styled(Title)`
  text-align: center;
  flex-grow: 1;
`;

export type ContactDetailsPageProps = {
  state: State;
  nextPage: (payload: Partial<State>) => void;
  prevPage: (payload: Partial<State>) => void;
};

export const ContactDetailsPage: React.FC<ContactDetailsPageProps> = ({
  state,
  nextPage,
  prevPage,
}) => {
  const { t } = useTranslation();
  const [mobileAlpha2Code] = useState(
    (state.contactDetails?.MobileAlpha2Code ||
      state.crossSellSetup?.Iso2CountryCode ||
      "GB") as string
  );
  const [mobileCountryCode] = useState(
    parseInt(
      (state.contactDetails?.MobileCountryCode ||
        state.crossSellSetup?.CountryCode ||
        "44") as string
    )
  );
  const [phoneAlpha2Code] = useState(
    (state.contactDetails?.PhoneAlpha2Code ||
      state.crossSellSetup?.Iso2CountryCode) as string | undefined
  );
  const [phoneCountryCode] = useState(
    parseInt(
      (state.contactDetails?.PhoneCountryCode ||
        state.crossSellSetup?.CountryCode ||
        "44") as string
    )
  );

  const [contactDetails] = useState({
    ...state.contactDetails,
    MobileAlpha2Code: mobileAlpha2Code,
    MobileCountryCode: mobileCountryCode,
    PhoneAlpha2Code: state.crossSellSetup?.ThemeSettings
      ?.ShowTelephoneNumberField
      ? phoneAlpha2Code
      : undefined,
    PhoneCountryCode: state.crossSellSetup?.ThemeSettings
      ?.ShowTelephoneNumberField
      ? phoneCountryCode
      : undefined,
    CustomField: state.contactDetails?.CustomField,
  } as ContactDetails);

  const onClickNextPage = (data: ContactDetails) => {
    const updatedState = {
      ...state,
      contactDetails: data,
    };

    nextPage(updatedState);
  };

  const onClickPrevPage = () => {
    const updatedState = {
      ...state,
      contactDetails: contactDetails,
    };

    if (
      state.crossSellSetup?.UseNewSkipTimeSelectFlow &&
      !state.bookingDetails.timeSlot?.TimeSlot.AvailablePromotions?.length &&
      state.availabilityType === "full"
    ) {
      delete updatedState.bookingDetails.timeSlot;
      updatedState.availabilityResponses = [];
      updatedState.searchedCrossSellVenues = [];
      updatedState.availableCrossSellVenues = [];
    }

    prevPage(updatedState);
  };

  return (
    <StyledPageContainer>
      <Logo styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles} />
      <StyledHeader>
        <IconButton
          icon={ArrowLeftSolid}
          ariaLabel={t("Previous")}
          onClick={onClickPrevPage}
          styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
          dataId="back-button"
        />
        <StyledTitle
          styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
        >
          {t("ContactDetails")}
        </StyledTitle>
      </StyledHeader>
      <DetailsForm
        styles={state.crossSellSetup?.ThemeStyles || defaultThemeStyles}
        optionalFormFields={{
          showTitle:
            state.crossSellSetup?.ThemeSettings?.ShowCustomerTitle || false,
          showTelephoneNumber:
            state.crossSellSetup?.ThemeSettings?.ShowTelephoneNumberField ||
            false,
          showCompany:
            state.crossSellSetup?.ThemeSettings?.ShowCompanyField || false,
          showPostcode:
            state.crossSellSetup?.ThemeSettings?.ShowPostcodeField || false,
          postcodeRequired:
            state.crossSellSetup?.ThemeSettings?.PostcodeRequired || false,
          showHouseholds:
            state.crossSellSetup?.ThemeSettings?.ShowHouseholdsField || false,
          showComments:
            state.crossSellSetup?.ThemeSettings?.ShowCommentsField || false,
          showDateOfBirth:
            state.crossSellSetup?.ThemeSettings?.ShowDateOfBirth || false,
          showDateOfBirthYear:
            state.crossSellSetup?.ThemeSettings?.ShowDateOfBirthYear || false,
          dateOfBirthRequired:
            state.crossSellSetup?.ThemeSettings?.DateOfBirthRequired || false,
          dateOfBirthDescription:
            state.crossSellSetup?.ThemeSettings?.DateOfBirthDescription || "",
          showSmsMarketing:
            state.crossSellSetup?.ThemeSettings?.ShowSmsMarketingOption ||
            false,
          showGroupMarketing:
            state.crossSellSetup?.IsGroupMarketingEnabled || false,
          showCustomField: state.customField ? true : false,
          showMembershipId:
            state.crossSellSetup?.ThemeSettings?.ShowMembershipIdField || false,
          membershipIdRequired:
            state.crossSellSetup?.ThemeSettings?.IsMembershipIdRequired ||
            false,
          membershipIdTitle:
            state.crossSellSetup?.ThemeSettings?.MembershipIdTitle || "",
          precheckVenueEmailOptin:
            state.crossSellSetup?.ThemeSettings?.PrecheckVenueEmailOptin ||
            false,
          showTurnstile: state.crossSellSetup?.UseTurnstile || false,
        }}
        formData={contactDetails}
        setFormData={onClickNextPage}
        state={state}
      ></DetailsForm>
    </StyledPageContainer>
  );
};

export default ContactDetailsPage;
